import React, { useEffect, useCallback, useRef, forwardRef } from 'react'
import { Field } from 'formular'
import { useForceUpdate } from 'sb-hooks'

import UnderlineView from './UnderlineView/UnderlineView'


export type FormularSelectFieldProps = {
  id?: string
  field: Field<any>
  type?: string
  label?: string | ReactIntlMessage
  placeholder?: string | ReactIntlMessage
  icon?: string
  iconColor?: string
  errored?: boolean
  loading?: boolean
  readOnly?: boolean
  focusOnInit?: boolean
  withBorder?: boolean
  withEraser?: boolean
  standardView?: boolean
  blackOnFocus?: boolean
  autoComplete?: string
  dataTestId?: string
  dataRecurly?: string
  options: any[]
  onChange?: (event: EventTarget) => void
  onBlur?: (event: EventTarget) => void
  onFocus?: (event: EventTarget) => void
  onSubmit?: (event: EventTarget) => void
  onIconClick?: (event: EventTarget) => void
}

const FormularSelectField: React.FunctionComponent<FormularSelectFieldProps> = forwardRef((props, forwardedRef: any) => {
  const {
    field, type, label, placeholder,
    icon, iconColor,
    errored, loading, readOnly, focusOnInit, withBorder, withEraser, standardView,
    onSubmit, onIconClick, onBlur, onFocus, onChange,
    dataRecurly, dataTestId, id, options,

    // 'nope' was a hack to remove autoComplete, coz 'off' doesn't work
    // bug: https://bugs.chromium.org/p/chromium/issues/detail?id=914451
    // workarond: https://goo.gl/PeZ856
    // 'nope' is ignored? https://github.com/Rendez/downshift/commit/bf876844925545bab3cb32d6ff66c1beeb056199
    autoComplete = 'off',

    // test for /subscribe-prototype
    blackOnFocus,
  } = props

  const ref = useRef<HTMLInputElement>(null)
  const forceUpdate = useForceUpdate()

  useEffect(() => {
    if (focusOnInit) {
      ref.current.focus()
    }

    field.setRef(ref.current)

    field.on('change', forceUpdate)
    field.on('unset', forceUpdate)
    field.on('validate', forceUpdate)

    return () => {
      field.unsetRef()
      field.off('change', forceUpdate)
      field.off('unset', forceUpdate)
      field.off('validate', forceUpdate)
    }
  }, [ field ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((event) => {
    let { value } = event.target

    field.set(value)

    if (typeof onChange === 'function') {
      onChange(event)
    }
  }, [ field, onChange ])

  const handleBlur = useCallback(async (event) => {
    await field.debounceValidate()

    if (typeof onBlur === 'function') {
      onBlur(event)
    }
  }, [ field, onBlur ])

  const handleErase = useCallback(() => {
    field.unset()
  }, [ field ])

  const resolveRef = (node) => {
    ref.current = node

    if (forwardedRef) {
      forwardedRef.current = node
    }
  }

  const nodeProps: any = {
    id,
    ref: resolveRef,
    type,
    label,
    value: field.state.value,
    error: field.state.error,
    errored,
    loading,
    readOnly,
    disabled: readOnly,
    withEraser,
    blackOnFocus, // test for /subscribe-prototype
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus,
    onErase: handleErase,
    dataRecurly,
    dataTestId,
    options,
    autoComplete,
  }

  if (standardView) {
    nodeProps.placeholder = placeholder
    nodeProps.withBorder = withBorder
    nodeProps.onSubmit = onSubmit
  }
  else {
    nodeProps.icon = icon
    nodeProps.iconColor = iconColor
    nodeProps.onIconClick = onIconClick
  }

  return React.createElement(UnderlineView, nodeProps)
})


export default FormularSelectField
